import { BatchSummary } from '../../core/services/batch';
import { BrainPalette } from './brain-flow';

export interface DocumentSourceTheme {
  key: string;
  label: string;
  shortLabel: string;
  icon: string;
  palette: BrainPalette;
  iconBg: string;
  cardBorder: string;
  cardShadow: string;
  disc: string;
  discStroke: string;
  halo: string;
  labelColor: string;
  pillBg: string;
  pillFg: string;
  bar: string;
}

const SHAREPOINT: DocumentSourceTheme = {
  key: 'SharePoint',
  label: 'SharePoint',
  shortLabel: 'SharePoint',
  icon: 'sharepoint',
  palette: {
    fill: '#7EE0D6',
    stroke: '#038387',
    glow: '3, 131, 135',
    particle: [3, 131, 135]
  },
  iconBg: '#F0FDFA',
  cardBorder: '#99F6E4',
  cardShadow: 'rgba(20, 184, 166, 0.12)',
  disc: '#F0FDFA',
  discStroke: '#CCFBF1',
  halo: '#14B8A6',
  labelColor: '#0D9488',
  pillBg: '#CCFBF1',
  pillFg: '#0D9488',
  bar: '#37C6D0'
};

const GOOGLE_DRIVE: DocumentSourceTheme = {
  key: 'GoogleDrive',
  label: 'Google Drive',
  shortLabel: 'Google Drive',
  icon: 'googledrive',
  palette: {
    fill: '#F4B400',
    stroke: '#0F9D58',
    glow: '15, 157, 88',
    particle: [15, 157, 88]
  },
  iconBg: '#F0FDF4',
  cardBorder: '#86EFAC',
  cardShadow: 'rgba(15, 157, 88, 0.16)',
  disc: '#F0FDF4',
  discStroke: '#BBF7D0',
  halo: '#0F9D58',
  labelColor: '#0F9D58',
  pillBg: '#DCFCE7',
  pillFg: '#166534',
  bar: '#0F9D58'
};

const SLACK: DocumentSourceTheme = {
  key: 'Slack',
  label: 'Slack',
  shortLabel: 'Slack',
  icon: 'slack',
  palette: {
    fill: '#E01E5A',
    stroke: '#611F69',
    glow: '97, 31, 105',
    particle: [97, 31, 105]
  },
  iconBg: '#FDF2F8',
  cardBorder: '#F0ABFC',
  cardShadow: 'rgba(97, 31, 105, 0.16)',
  disc: '#FDF4FF',
  discStroke: '#F5D0FE',
  halo: '#611F69',
  labelColor: '#611F69',
  pillBg: '#FAE8FF',
  pillFg: '#611F69',
  bar: '#611F69'
};

const TEAMS: DocumentSourceTheme = {
  key: 'Teams',
  label: 'Microsoft Teams',
  shortLabel: 'Teams',
  icon: 'teams',
  palette: {
    fill: '#7B83EB',
    stroke: '#4B53BC',
    glow: '75, 83, 188',
    particle: [75, 83, 188]
  },
  iconBg: '#EEF2FF',
  cardBorder: '#C7D2FE',
  cardShadow: 'rgba(75, 83, 188, 0.16)',
  disc: '#EEF2FF',
  discStroke: '#E0E7FF',
  halo: '#4B53BC',
  labelColor: '#4338CA',
  pillBg: '#E0E7FF',
  pillFg: '#4338CA',
  bar: '#4B53BC'
};

const FTP: DocumentSourceTheme = {
  key: 'Ftp',
  label: 'FTP / SFTP',
  shortLabel: 'FTP',
  icon: 'ftp',
  palette: {
    fill: '#67E8F9',
    stroke: '#0E7490',
    glow: '14, 116, 144',
    particle: [14, 116, 144]
  },
  iconBg: '#ECFEFF',
  cardBorder: '#A5F3FC',
  cardShadow: 'rgba(14, 116, 144, 0.16)',
  disc: '#ECFEFF',
  discStroke: '#CFFAFE',
  halo: '#0891B2',
  labelColor: '#0E7490',
  pillBg: '#CFFAFE',
  pillFg: '#0E7490',
  bar: '#06B6D4'
};

const UPLOAD: DocumentSourceTheme = {
  key: 'Upload',
  label: 'Local upload',
  shortLabel: 'Upload',
  icon: 'upload',
  palette: {
    fill: '#FDBA74',
    stroke: '#C2410C',
    glow: '194, 65, 12',
    particle: [194, 65, 12]
  },
  iconBg: '#FFF7ED',
  cardBorder: '#FDBA74',
  cardShadow: 'rgba(194, 65, 12, 0.16)',
  disc: '#FFF7ED',
  discStroke: '#FFEDD5',
  halo: '#EA580C',
  labelColor: '#C2410C',
  pillBg: '#FFEDD5',
  pillFg: '#C2410C',
  bar: '#F97316'
};

export const DOCUMENT_SOURCES: Record<string, DocumentSourceTheme> = {
  SharePoint: SHAREPOINT,
  GoogleDrive: GOOGLE_DRIVE,
  Slack: SLACK,
  Teams: TEAMS,
  Ftp: FTP,
  Upload: UPLOAD
};

const DOCUMENT_SOURCE_ORDER = ['SharePoint', 'GoogleDrive', 'Slack', 'Teams', 'Ftp', 'Upload'];

export function themeForProvider(provider: string): DocumentSourceTheme {
  return DOCUMENT_SOURCES[provider] ?? {
    key: provider,
    label: provider,
    shortLabel: provider,
    icon: 'generic',
    palette: {
      fill: '#CBD5E1',
      stroke: '#475569',
      glow: '71, 85, 105',
      particle: [71, 85, 105]
    },
    iconBg: '#F8FAFC',
    cardBorder: '#CBD5E1',
    cardShadow: 'rgba(71, 85, 105, 0.14)',
    disc: '#F8FAFC',
    discStroke: '#E2E8F0',
    halo: '#64748B',
    labelColor: '#475569',
    pillBg: '#F1F5F9',
    pillFg: '#475569',
    bar: '#64748B'
  };
}

/** Document sources that can appear on the brain. GitHub and mail have their own lanes. */
export function documentProvider(batch: BatchSummary): string | null {
  const type = batch.sourceType;
  if (type === 'Mailbox' || type === 'Github' || type === 'GitHub') return null;
  if (batch.sourceProvider) return batch.sourceProvider;
  if (type === 'Upload') return 'Upload';
  if (type === 'SharePoint') return 'SharePoint';
  if (type === 'Connector') return 'Connector';
  return type || 'SharePoint';
}

export function connectorPresentation(provider: string | null | undefined): { label: string; icon: string } {
  const key = (provider || '').trim();
  if (key === 'Mailbox' || key.toLowerCase() === 'outlook') return { label: 'Outlook', icon: 'outlook' };
  if (key === 'Github' || key === 'GitHub') return { label: 'GitHub', icon: 'github' };
  if (key === 'Upload') return { label: 'Local upload', icon: 'upload' };
  if (!key || key === 'SharePoint') return { label: 'SharePoint', icon: 'sharepoint' };
  const theme = themeForProvider(key);
  return { label: theme.label, icon: theme.icon };
}

export function compareDocumentProviders(a: string, b: string): number {
  const ia = DOCUMENT_SOURCE_ORDER.indexOf(a);
  const ib = DOCUMENT_SOURCE_ORDER.indexOf(b);
  return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
}
